// Third Party
import * as React from 'react';
import Image from 'next/image';

// Utils
import { usePlacesAutocompleteQuery, PlaceTypeFilter } from '@gql'; // TODO - remove this!
import { useDebounce } from '@hooks';
import { GRAY_PIXEL_DATA_URL } from '@utils/constants';
import { useRegionsListAllFeaturedQuery } from '@gql';
import { imagekitLoader } from '@utils/imagekit-loader';
import { concatenateAndReplace } from '@utils/test-helpers';

// UI
import { Icon, IconNames } from '@components/icon';
import boothbayHarborMe from '../../../public/assets/img/featured-searches/boothbay-harbor-me.png';
import capeMayNJ from '../../../public/assets/img/featured-searches/cape-may-nj.png';
import hawaii from '../../../public/assets/img/featured-searches/hawaii.png';
import austin from '../../../public/assets/img/featured-searches/austin-tx.png';
import breck from '../../../public/assets/img/featured-searches/breck-co.png';
import { Button } from '@components/button';
import { IPlacesSelectAutocompleteOption } from '@components/places-select-autocomplete';
import { Typography } from '@components/typography';
import { ButtonIcon } from '@components/button-icon';
import { extractFirstPhraseBeforeComma } from '@utils/extract-first-phrase-before-comma';

// Suggestions shown if there are no featured
const suggestions = [
  {
    name: 'Austin, TX',
    imageUrl: austin,
    placeId: 'ChIJLwPMoJm1RIYRetVp1EtGm10',
  },
  {
    name: 'Breckenridge, CO',
    imageUrl: breck,
    placeId: 'ChIJwecmbD32aocReqKAZn',
  },
  {
    name: 'Boothbay Harbor, ME',
    imageUrl: boothbayHarborMe,
    placeId: 'ChIJ69Mkld6YrUwREWQ8hGp28MM',
  },
  {
    name: 'Cape May, NJ',
    imageUrl: capeMayNJ,
    placeId: 'ChIJlffuOkRUv4kRovRz4Zz3t8I',
  },
  {
    name: 'Hawaii',
    imageUrl: hawaii,
    placeId: 'ChIJBeB5Twbb_3sRKIbMdNKCd0s',
  },
];

const imageSizePx = 16 * 4;

export enum PlacesSelectAutocompleteFilters {
  ALL = 'ALL',
  ADDRESS = 'ADDRESS',
  ESTABLISHMENT = 'ESTABLISHMENT',
  GEOCODE = 'GEOCODE',
  REGIONS = 'REGIONS',
}

type Props = {
  onChange?: (
    name: string,
    value: IPlacesSelectAutocompleteOption | null
  ) => void;
  onClear: VoidFunction;
  initialValue?: IPlacesSelectAutocompleteOption | null;
  placeName: string;
};

export const MobileSelectAutocomplete: React.FC<Props> = ({
  onChange,
  onClear,
  initialValue = null,
  placeName,
}) => {
  const { data: destinations, loading } = useRegionsListAllFeaturedQuery({
    variables: { page: 1, pageSize: 20 },
  });

  const [searchInput, setSearchInput] = React.useState('');
  const [value, setValue] =
    React.useState<IPlacesSelectAutocompleteOption | null>(initialValue);
  const [options, setOptions] = React.useState<
    IPlacesSelectAutocompleteOption[]
  >([]);

  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const containerRef = React.useRef<HTMLInputElement | null>(null);

  const debouncedSearchValue = useDebounce(searchInput, 300);

  const { data } = usePlacesAutocompleteQuery({
    variables: {
      query: debouncedSearchValue || '',
      type: PlaceTypeFilter.Regions,
    },
  });

  React.useEffect(() => {
    if (data?.placesAutocomplete) {
      setOptions(
        data.placesAutocomplete.map((place) => ({
          label: place.name,
          value: place.id,
        }))
      );
    }
  }, [data, value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const inputValue = event.target.value;
    if (containerRef.current) {
      containerRef.current.scrollLeft = 0;
    }
    setSearchInput(inputValue);
  };

  const onSelect = (
    name: string,
    value: IPlacesSelectAutocompleteOption | null
  ) => {
    setValue(value);
    onChange?.(name, value);
    if (inputRef.current && value && value.label) {
      inputRef.current.value = value.label;
    }
  };

  const clearInput = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const searchResultsPresent = options.length > 0;

  return (
    <div className="w-full h-full bg-white text-gray-dark">
      <div className="flex flex-row items-center p-2 mx-2 border-2 border-primary-main rounded-full">
        <input
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={true}
          ref={inputRef}
          type="text"
          onChange={handleChange}
          placeholder={
            placeName !== ''
              ? extractFirstPhraseBeforeComma(placeName)
              : 'Where to?'
          }
          className="pl-4 p-2 focus:outline-none w-full"
          data-testid="@mobile/search-input"
        />
        {placeName && placeName !== '' && (
          <ButtonIcon
            variant="text"
            color="gray"
            size="sm"
            icon={IconNames.CIRCLE_X}
            iconSize="lg"
            ariaDescribedby="clear search input"
            onClick={() => {
              onClear();
              clearInput();
              setOptions([]);
            }}
            className="ps-0"
            data-testid="@mobile/clear-search-input"
          />
        )}
      </div>

      <div className="flex flex-col pt-4">
        <div className="px-4 pb-2">
          <Typography variant="subtitle2" component="p">
            {searchResultsPresent ? 'Search Results' : 'Popular Getaways'}
          </Typography>
        </div>

        {searchResultsPresent ? (
          options &&
          options.slice(0, 5).map((option) => (
            <div
              key={option.value}
              className="flex flex-row w-full overflow-hidden"
            >
              <SearchResult
                key={option.value}
                name={option.label}
                imageElement={
                  <div className="bg-gray-xlight w-full h-full flex flex-col justify-center items-center">
                    <Icon
                      name={IconNames.LOCATION_DOT}
                      solid={true}
                      className="w-4 h-4"
                    />
                  </div>
                }
                imageHeight={4}
                imageWidth={4}
                onSelect={() => onSelect(option.label, option)}
              />
            </div>
          ))
        ) : (
          <div ref={containerRef} className="flex flex-row overflow-x-auto">
            {destinations &&
              destinations?.regionsListAllFeatured.entries.map(
                (destination) => (
                  <div key={destination.name} className="flex-shrink-0 px-2">
                    <PopularGetaway
                      key={destination.name}
                      name={destination.name}
                      imageElement={
                        <div className="relative">
                          <Image
                            layout="fixed"
                            width={100}
                            height={40}
                            loader={imagekitLoader}
                            className="aspect-[1] object-cover object-center"
                            src={destination.imageUrl || ''}
                            alt={destination.name || 'search result image'}
                            placeholder="blur"
                            blurDataURL={GRAY_PIXEL_DATA_URL}
                          />
                        </div>
                      }
                      onSelect={() =>
                        onSelect(destination.name, {
                          label: destination.name,
                          value: destination.placeId,
                        })
                      }
                    />{' '}
                  </div>
                )
              )}
          </div>
        )}

        {!searchResultsPresent &&
          !loading &&
          !destinations &&
          suggestions.slice(0, 5).map((suggestion) => (
            <SearchResult
              key={suggestion.name}
              name={suggestion.name}
              imageElement={
                <div className="relative">
                  <Image
                    layout="fixed"
                    width={imageSizePx}
                    height={imageSizePx}
                    className="object-center object-cover"
                    src={suggestion.imageUrl}
                    alt={suggestion.name || 'search result image'}
                    placeholder="blur"
                    blurDataURL={GRAY_PIXEL_DATA_URL}
                  />
                </div>
              }
              imageHeight={12}
              imageWidth={12}
              onSelect={() =>
                onSelect(suggestion.name, {
                  label: suggestion.name,
                  value: suggestion.placeId,
                })
              }
            />
          ))}
      </div>
    </div>
  );
};

const PopularGetaway: React.FC<{
  name: string;
  imageElement: JSX.Element;
  onSelect?: VoidFunction;
}> = ({ name, imageElement, onSelect }) => {
  return (
    <Button
      onClick={onSelect}
      className="flex flex-col align-items-start hover:bg-gray-xlight pb-2 pt-3 justify-start space-y-2"
      color="gray"
      variant="text"
    >
      {imageElement && (
        <div className="w-min">
          <div className="w-16 h-16 rounded-lg flex overflow-hidden">
            {imageElement}
          </div>
        </div>
      )}
      <span
        className="text-primary-main text-left font-semibold text-sm"
        data-testid={`@search/${concatenateAndReplace(name)}`}
      >
        {name}
      </span>
    </Button>
  );
};

const SearchResult: React.FC<{
  name: string;
  imageElement: JSX.Element;
  imageHeight: number;
  imageWidth: number;
  onSelect?: VoidFunction;
}> = ({ name, imageElement, imageHeight, imageWidth, onSelect }) => {
  return (
    <Button
      onClick={onSelect}
      className="flex flex-row w-full align-items-start hover:bg-gray-xlight justify-start"
      color="gray"
      variant="text"
    >
      {imageElement && (
        <div className="w-min">
          <div
            className={`w-${imageWidth} h-${imageHeight} rounded-lg flex overflow-hidden`}
          >
            {imageElement}
          </div>
        </div>
      )}
      <span
        className="flex overflow-hidden ml-4 text-primary-main text-left font-semibold text-sm"
        data-testid={`@search/${concatenateAndReplace(name)}`}
      >
        {name}
      </span>
    </Button>
  );
};
